import {useFarm} from '../services/farmContext';
import {usePersonalSettings} from '../services/personalSettings';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, ZoomControl, TileLayer, Polygon, Polyline, CircleMarker, Marker, Tooltip, useMap, useMapEvents, LayersControl, LayerGroup, Circle } from 'react-leaflet';
import { LiveUserLocation, MapFeature, GeometryType, MapFeatureType } from '../types';
import { useLayout } from './Layout';
import L from 'leaflet';
import { useGpsTracking } from '../services/useGpsTracking';
import {useCompassHeading} from '../services/useCompassHeading';
import {useGpsWorkspace} from '../services/GpsWorkspaceContext';
import {useRouteRecording} from '../services/RouteRecordingContext';
import { useAuth } from '../services/authContext';
import { pointStyle, pointColor, pointKind, isPhotoPlace, mapCoordinates } from '../services/placeModel';
import clsx from 'clsx';
import { X, LocateFixed } from 'lucide-react';
import { MAP_TILE_URLS } from '../services/mapTileUrls';
import RecordedRouteLayer from './RecordedRouteLayer';
import MapCredits from './MapCredits';
import OfflineImageryLayer from './OfflineImageryLayer';
import MapEnvironment from './MapEnvironment';
import {usePreparedTerrain} from '../services/preparedMap';
import MapLayerMenu from './MapLayerMenu';
import NeighborParcelsLayer from './NeighborParcelsLayer';

const renderHeadingArrow = (headingDeg?: number, color = '#ffffff') => {
  if (typeof headingDeg !== 'number' || !Number.isFinite(headingDeg)) return '';
  return `<div style="position:absolute;left:50%;top:50%;width:2px;height:14px;transform:translate(-50%,-100%) rotate(${headingDeg}deg);transform-origin:50% 100%;pointer-events:none;"><div style="position:absolute;left:50%;top:-8px;width:0;height:0;transform:translateX(-50%);border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:8px solid ${color};filter:drop-shadow(0 0 2px rgba(0,0,0,0.5));"></div></div>`;
};

const buildGpsMarkerHtml = (color: string, label: string, headingDeg?: number, lastKnown = false) => {
  const safeLabel = label.replace(/[&<>"']/g, '');
  return `<div style="position:relative;width:22px;height:22px;">${lastKnown ? '' : renderHeadingArrow(headingDeg)}<div style="width:22px;height:22px;border-radius:50%;background:${lastKnown ? '#64748b' : color};border:3px ${lastKnown ? 'dashed' : 'solid'} white;box-shadow:0 0 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:9px;font-weight:700;">${safeLabel}</div>${lastKnown ? '<span style="position:absolute;right:-5px;bottom:-5px;background:white;border-radius:50%;font-size:13px;color:#475569;line-height:16px;">◷</span>' : ''}</div>`;
};

interface MapComponentProps {
  features: MapFeature[];
  center?: [number, number];
  zoom?: number;
  onFeatureClick?: (feature: MapFeature) => void;
  selectedFeatureId?: string | null;
  onMapClick?: (coordinates: [number, number]) => void;
  draftPoint?: [number, number] | null;
  geofenceGeoJson?: any;
  gpsTrackingAllowed?: boolean;
  isLocationPanelOpen?: boolean;
  onCloseLocationPanel?: () => void;
  viewResetToken?: number;
  restoreView?: {center:[number,number];zoom:number};
  onViewChange?: (view:{center:[number,number];zoom:number})=>void;
  onSelectionClose?: () => void;
}

const MapSelectionDismissal:React.FC<{onClose:()=>void}>=({onClose})=>{
 useMapEvents({click:onClose});
 useEffect(()=>{
  const close=(event:KeyboardEvent)=>{if(event.key==='Escape')onClose();};
  window.addEventListener('keydown',close);return()=>window.removeEventListener('keydown',close);
 },[onClose]);
 return null;
};

const MapViewContext:React.FC<{restoreView?:{center:[number,number];zoom:number};onViewChange?:MapComponentProps['onViewChange']}> = ({restoreView,onViewChange}) => {
 const restored=useRef(false);
 const map=useMapEvents({moveend:()=>onViewChange?.({center:[map.getCenter().lat,map.getCenter().lng],zoom:map.getZoom()})});
 useEffect(()=>{if(restoreView&&!restored.current){restored.current=true;map.setView(restoreView.center,restoreView.zoom,{animate:false});}},[map,restoreView]);
 return null;
};

// Apply the new map viewport after the desktop sidebar width transition.
const MapResizeHandler: React.FC<{onSidebarResize:()=>void}> = ({onSidebarResize}) => {
  const map=useMap();
  const {isCollapsed}=useLayout();
  const previous=useRef(isCollapsed);
  const focus=useRef(onSidebarResize);
  focus.current=onSidebarResize;
  useEffect(()=>{
    const changed=previous.current!==isCollapsed;
    previous.current=isCollapsed;
    map.invalidateSize({pan:false});
    const timer=window.setTimeout(()=>{
      map.invalidateSize({pan:false});
      if(changed && window.matchMedia('(min-width: 1024px)').matches) focus.current();
    },320);
    return ()=>window.clearTimeout(timer);
  },[isCollapsed,map]);
  return null;
};

// Component to adjust map bounds based on features
const MapBounds: React.FC<{ features: MapFeature[] }> = ({ features }) => {
  const map = useMap();

  useEffect(() => {
    let fitTimer:number|undefined;
    if (features.length > 0) {
      // Prioritize Farm Boundaries for centering if they exist
      // This ensures the map zooms to the property lines rather than including distant outlying fields or POIs if possible
      const farmBoundaries = features.filter(f => f.entity_type === 'farm' || f.feature_type === MapFeatureType.Farm);
      const targetFeatures = farmBoundaries.length > 0 ? farmBoundaries : features;

      try {
        const bounds = L.latLngBounds([]);
        let hasValidBounds = false;
        
        const extendBounds = (coords: any) => {
            if (!Array.isArray(coords)) return;
            // Check for simple [lat, lng] pair
            if (coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                bounds.extend(coords as [number, number]);
                hasValidBounds = true;
                return;
            }
            // Recursively check nested arrays
            coords.forEach(child => {
                if (child) extendBounds(child);
            });
        };

        targetFeatures.forEach(f => {
            if (!f.coordinates_geojson) return;
            try {
                const coords = mapCoordinates(f);
                if (coords) extendBounds(coords);
            } catch (e) {
                // ignore invalid json
            }
        });

        if (hasValidBounds && bounds.isValid()) {
            // Add a small delay to ensure container sizing is finalized before fitting
            fitTimer=window.setTimeout(() => {
                // Initial framing must not leave a zoom transition running when a
                // photo link immediately unmounts this map (Leaflet's timer outlives it).
                map.fitBounds(bounds, { padding: [20, 20], maxZoom: 18, animate: false });
            }, 100);
        }
      } catch (e) {
        console.warn("Error calculating bounds", e);
      }
    }
    return()=>window.clearTimeout(fitTimer);
  }, [features, map]);

  return null;
};

const PlaceMapClick: React.FC<{ onClick?: (coordinates: [number,number]) => void }> = ({ onClick }) => {
  useMapEvents({ click: e => onClick?.([e.latlng.lat,e.latlng.lng]) });
  return null;
};

const CurrentLocationMarker: React.FC<{ position: GeolocationPosition | null; headingDeg?: number | null; headingSource?: string; lastKnown: boolean }> = ({ position, headingDeg, headingSource, lastKnown }) => {
  if (!position) return null;

  const latest: [number, number] = [position.coords.latitude, position.coords.longitude];
  const gpsMarkerIcon = L.divIcon({
    className: 'gps-live-marker',
    html: buildGpsMarkerHtml('rgba(59,130,246,0.9)', '', headingDeg ?? undefined, lastKnown),
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

  return (
    <>
      <Circle
        center={latest}
        radius={position.coords.accuracy || 15}
        pathOptions={{ color: '#2563eb', weight: 1, opacity: 0.25, fillOpacity: 0.08 }}
      />
      <Marker position={latest} icon={gpsMarkerIcon}>
        <Tooltip>You · {lastKnown ? 'Last seen' : 'Live'} · {formatRelativeTime(position.timestamp)}{!lastKnown&&headingDeg!=null ? ` · ${headingSource==='compass'?'Phone direction':'Travel direction'}`:''}</Tooltip>
      </Marker>
    </>
  );
};

const formatRelativeTime = (ts: number) => {
  const diff = Math.max(0, Date.now() - ts);
  const seconds = Math.round(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
};



const colorFromString = (input: string) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 75%, 55%)`;
};

const getInitials = (name?: string, fallback?: string) => {
  const source = name || fallback || '';
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0 && fallback) return fallback.slice(0, 2).toUpperCase();
  const initials = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');
  return initials || '•';
};

const PeerLocationMarker: React.FC<{ location: LiveUserLocation }> = ({ location }) => {
  const { coords } = location;
  const position: [number, number] = [coords.lat, coords.lng];
  const markerColor = useMemo(() => colorFromString(location.userEmail || location.userSub), [location.userEmail, location.userSub]);
  const initials = useMemo(() => getInitials(location.userName, location.userEmail), [location.userName, location.userEmail]);
  const peerIcon = useMemo(() => L.divIcon({
    className: 'gps-peer-marker',
    html: buildGpsMarkerHtml(markerColor, initials, location.coords.headingDeg, !location.isLive),
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  }), [initials, location.coords.headingDeg, markerColor, location.isLive]);

  return (
    <>
      {coords.accuracyM && (
        <Circle
          center={position}
          radius={coords.accuracyM}
          pathOptions={{ color: markerColor, weight: 1, opacity: 0.2, fillOpacity: 0.05 }}
        />
      )}
      <Marker position={position} icon={peerIcon}>
        <Tooltip direction="top" offset={[0, -8]} opacity={1}>
          <div className="text-xs font-semibold">{location.userName || location.userEmail}</div>
          <div className="text-[11px] text-gray-700">{location.isLive ? 'Live' : 'Last known'} · {formatRelativeTime(coords.ts)}</div>
        </Tooltip>
      </Marker>
    </>
  );
};

const PointFeatureMarker:React.FC<{feature:MapFeature;center:[number,number];isSelected:boolean;isInteractive:boolean;eventHandlers?:L.LeafletEventHandlerFnMap}>=({feature,center,isSelected,isInteractive,eventHandlers})=>{
 const {activeFarm}=useFarm();
 const symbol = pointStyle(feature);
 const color = pointColor(feature,activeFarm?.poi_colors);
 const markerColor=isSelected?color:`color-mix(in srgb, ${color} 72%, #596b5d 28%)`;
 // Touch browsers may use the first tap to reveal hover/focus content.
 // On those devices, label and detail must share the same selection.
 const showLabel=isSelected||window.matchMedia('(hover: hover) and (pointer: fine)').matches;
 const icon = useMemo(()=>L.divIcon({ className: 'place-point-marker', iconSize: [44,44], iconAnchor: [22,22],
   html: '<div style="width:44px;height:44px;display:grid;place-items:center"><div style="width:30px;height:30px;border-radius:10px;background:'+markerColor+';border:'+(isSelected?'2px solid #c6aa67':'1.5px solid #ffffffb3')+';box-shadow:'+(isSelected?'0 0 0 3px #f0e6c566':'0 1px 3px #0003')+';display:grid;place-items:center"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#f5f4eb" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"><path d="'+symbol.path+'"/></svg></div></div>' }),[markerColor,isSelected,symbol.path]);
 return <Marker position={center} icon={icon} eventHandlers={eventHandlers} interactive={isInteractive} zIndexOffset={isSelected?300:0} title={feature.label || symbol.label}>
   {showLabel&&<Tooltip key={isSelected?'selected':'hover'} permanent={isSelected} direction="top" offset={[0,-16]}>{feature.label || symbol.label} · {symbol.label}{feature.is_inactive?' · Inactive':''}</Tooltip>}
 </Marker>;

};

const FeatureRenderer: React.FC<{ feature: MapFeature, onClick?: (f: MapFeature) => void, isSelected: boolean, isInteractive?: boolean }> = ({ feature, onClick, isSelected, isInteractive = true }) => {
  try {
    const positions = mapCoordinates(feature);
    
    // Safety check for empty geometry
    if (!positions || (Array.isArray(positions) && positions.length === 0)) return null;

    const eventHandlers = isInteractive && onClick
      ? {
          click: (e: any) => {
            L.DomEvent.stopPropagation(e);
            onClick(feature);
          }
        }
      : undefined;

    // Feature Type Checks
    const isTrail = feature.feature_type === MapFeatureType.Trail;
    const isStructure = feature.feature_type === MapFeatureType.Structure;
    // Treat as Land if explicit type OR if generic polygon (default)
    const isExplicitLand = [MapFeatureType.Field, MapFeatureType.Tract, MapFeatureType.Farm].includes(feature.feature_type as MapFeatureType);
    const isLand = isExplicitLand || (!isTrail && !isStructure && ['polygon','multipolygon'].includes(String(feature.geometry_type).toLowerCase()));
    
    // Style Calculation
    const baseColor = feature.color || '#3388ff';
    // For Trails/Structures/POI, ignore fill opacity from DB and force 0/transparent
    // For Land, use the DB value or default 0.2
    const opacity = isLand ? (feature.fill_opacity !== undefined ? feature.fill_opacity : 0.2) : 0;
    const fillColor = feature.fill_color || baseColor;
    const weight = Math.max(1,(feature.stroke_weight || 3)*0.65);

    const selectedStyle = { 
        color: '#c6aa67',
        fillColor: '#c6aa67',
        weight: Math.max(2.5,weight + 1),
        opacity: 1,
        fillOpacity: isLand ? Math.min(opacity + 0.1, 0.28) : 0,
        fill: isLand,
        dashArray: isTrail ? '10, 10' : undefined 
    }; 
    
    const defaultStyle = { 
        color: `color-mix(in srgb, ${baseColor} 65%, #96b0a3 35%)`,
        fillColor: fillColor,
        weight: weight, 
        opacity: 0.72,
        fillOpacity: opacity*0.45,
        fill: isLand && opacity > 0, 
        dashArray: isTrail ? '4, 7' : undefined
    };

    const style = isSelected ? selectedStyle : defaultStyle;
    
    if (String(feature.geometry_type).toLowerCase() === 'point') {
       // Robust check for center coordinate
       let center: [number, number] | null = null;
       if (Array.isArray(positions) && positions.length === 2 && typeof positions[0] === 'number') {
           center = positions as [number, number];
       } else if (Array.isArray(positions) && positions.length > 0 && Array.isArray(positions[0]) && positions[0].length === 2) {
           center = positions[0] as [number, number];
       }

       if (!center) return null;

       return <PointFeatureMarker feature={feature} center={center} isSelected={isSelected} isInteractive={isInteractive} eventHandlers={eventHandlers}/>;

    } else if (['polygon','multipolygon'].includes(String(feature.geometry_type).toLowerCase())) {
      return (
        <Polygon 
            positions={positions} 
            pathOptions={style} 
            eventHandlers={eventHandlers}
            interactive={isInteractive}
        >
             {feature.label && isSelected && (
                  <Tooltip direction="center" permanent>
                      {feature.label}
                  </Tooltip>
              )}
        </Polygon>
      );
    } else if (['polyline','linestring'].includes(String(feature.geometry_type).toLowerCase())) {
       return (
        <>
        {isInteractive&&<Polyline positions={positions} pathOptions={{color:'transparent',weight:14,opacity:0}} eventHandlers={eventHandlers} interactive />}
        <Polyline
            positions={positions} 
            pathOptions={style}
            eventHandlers={eventHandlers}
            interactive={isInteractive}
        >
             {feature.label && (
                  <Tooltip sticky>
                      {feature.label}
                  </Tooltip>
              )}
        </Polyline>
        </>
      );
    }
  } catch (e) {
    console.error("Failed to parse geometry", e);
  }
  return null;
};

const MapComponent: React.FC<MapComponentProps> = ({ 
    features, 
    center = [41.267718197887234, -89.49240997702736], 
    zoom = 15,
    onFeatureClick,
    selectedFeatureId,
    geofenceGeoJson,
    gpsTrackingAllowed = false,
    isLocationPanelOpen = false,
    onCloseLocationPanel,
    onMapClick,
    draftPoint,
    restoreView,viewResetToken=0,
    onViewChange,onSelectionClose
}) => {
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [layerControl,setLayerControl]=useState<L.Control.Layers|null>(null);
  const [stalenessNow, setStalenessNow] = useState<number>(Date.now());
  const [locationTtl, setLocationTtl] = useState(240 * 60000);
  const { user, idToken, privileges } = useAuth();
  const {activeFarm}=useFarm();
  const preparedTerrain=usePreparedTerrain(activeFarm?.farm_id,activeFarm?.boundary_geojson,user?.email,!!privileges.map?.view);
  const {settings:personalSettings,ready:preferencesReady,save:savePreferences}=usePersonalSettings(user?.email,user?.roles??(user?.role?[user.role]:[]));
  useEffect(()=>{
    if(!mapInstance||!preferencesReady)return;
    const container=mapInstance.getContainer();
    // Persist user changes only. Leaflet also emits overlay events during initialization
    // and controlled updates, which must not overwrite the saved preferences.
    const onLayerChange=(event:Event)=>{
      const input=event.target;
      if(!(input instanceof HTMLInputElement)||input.type!=='checkbox'||!input.closest('.leaflet-control-layers-overlays'))return;
      const name=input.closest('label')?.textContent?.trim();
      if(!name||!(name in personalSettings.layers)||personalSettings.layers[name]===input.checked)return;
      void savePreferences({layers:{[name]:input.checked}}).catch(error=>console.error('Map preference could not save',error));
    };
    container.addEventListener('change',onLayerChange,true);
    return()=>container.removeEventListener('change',onLayerChange,true);
  },[mapInstance,preferencesReady,personalSettings.layers,savePreferences]);
  const [sessionId] = useState(() => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)));
  const liveGps=useGpsWorkspace();
  const routeState=useRouteRecording();
  const gpsTracking = useGpsTracking({
    map: mapInstance,
    geofenceGeoJson,
    enabledByRole: gpsTrackingAllowed && preferencesReady,
    followPreference:personalSettings.followLocation,
    lastKnownTtlMs: locationTtl,
    zoomIncrease:liveGps.onPropertyZoomIncrease,
    routeViewId:routeState?.selectedRouteId,viewResetToken,
  });


  useEffect(() => {
    const timer = window.setInterval(() => setStalenessNow(Date.now()), 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => setLocationTtl(liveGps.lastKnownTtlMs), [liveGps.lastKnownTtlMs]);
  
  const { fieldFeatures, tractFeatures, farmFeatures, inactiveFeatures, blindFeatures, treeStandFeatures, poiFeatures, trailFeatures, structureFeatures, otherFeatures } = React.useMemo(() => {
      const fieldFeatures: MapFeature[] = [];
      const tractFeatures: MapFeature[] = [];
      const farmFeatures: MapFeature[] = [];
      const inactiveFeatures: MapFeature[] = [];
      const blindFeatures: MapFeature[] = [];
      const treeStandFeatures: MapFeature[] = [];
      const poiFeatures: MapFeature[] = [];
      const trailFeatures: MapFeature[] = [];
      const structureFeatures: MapFeature[] = [];
      const otherFeatures: MapFeature[] = [];

      features.forEach(f => {
          if (isPhotoPlace(f) && f.is_inactive) inactiveFeatures.push(f);
          else if (isPhotoPlace(f) && pointKind(f) === 'blind') blindFeatures.push(f);
          else if (isPhotoPlace(f) && pointKind(f) === 'tree_stand') treeStandFeatures.push(f);
          else if (f.feature_type === MapFeatureType.Trail) trailFeatures.push(f);
          else if (f.feature_type === MapFeatureType.Structure || isPhotoPlace(f) && ['barn','outbuilding','cabin'].includes(pointKind(f))) structureFeatures.push(f);
          else if (isPhotoPlace(f) || f.feature_type === MapFeatureType.PointOfInterest) poiFeatures.push(f);
          else if (f.feature_type === MapFeatureType.Field || f.entity_type === 'farm_field') fieldFeatures.push(f);
          else if (f.feature_type === MapFeatureType.Tract || f.entity_type === 'farm_tract') tractFeatures.push(f);
          else if (f.feature_type === MapFeatureType.Farm || f.entity_type === 'farm') farmFeatures.push(f);
          else otherFeatures.push(f);
      });

      return { fieldFeatures, tractFeatures, farmFeatures, inactiveFeatures, blindFeatures, treeStandFeatures, poiFeatures, trailFeatures, structureFeatures, otherFeatures };
  }, [features]);

  const statusText = React.useMemo(() => {
    if (!gpsTrackingAllowed) return 'Tracking unavailable for this role';
    if (gpsTracking.permissionState === 'unsupported') return 'Device does not support GPS';
    if (gpsTracking.permissionState === 'denied') return 'Permission not granted';
    if (gpsTracking.trackingState === 'error') return 'Location error';
    if (gpsTracking.trackingState === 'paused') return 'Location paused';
    if (gpsTracking.trackingState === 'stopped' || gpsTracking.trackingState === 'idle') return 'Location off';
    if (gpsTracking.trackingState === 'starting') return 'Locating...';
    if (gpsTracking.lastFix) return gpsTracking.inGeofence === false ? 'Off property' : 'On property';
    return 'Location off';
  }, [gpsTracking.inGeofence, gpsTracking.lastFix, gpsTracking.permissionState, gpsTracking.trackingState, gpsTrackingAllowed]);

  const permissionMessage = useMemo(() => {
    if (gpsTracking.permissionState === 'granted' && !gpsTracking.lastFix) {
      return 'Permission granted, but a location fix is currently unavailable.';
    }
    if (gpsTracking.permissionState === 'prompt') {
      return 'Allow location when your browser asks. If no prompt appears, check this site’s location permission.';
    }
    return null;
  }, [gpsTracking.lastFix, gpsTracking.permissionState]);


  const staleFixAgeMs = gpsTracking.lastSuccessfulFixTs
    ? Math.max(0, stalenessNow - gpsTracking.lastSuccessfulFixTs)
    : null;
  const isFixStale = staleFixAgeMs !== null && (staleFixAgeMs > liveGps.liveFreshMs || !['tracking','starting'].includes(gpsTracking.trackingState));
  const compass = useCompassHeading(gpsTrackingAllowed && preferencesReady && gpsTracking.enabled && !isFixStale, gpsTracking.lastFix);
  const stalenessText = gpsTracking.lastSuccessfulFixTs
    ? formatRelativeTime(gpsTracking.lastSuccessfulFixTs)
    : 'Waiting for your location';

  const accuracyText = gpsTracking.lastFix?.coords?.accuracy
    ? `${Math.round(gpsTracking.lastFix.coords.accuracy)}m`
    : '—';

  const liveStatusText = useMemo(() => {
    if (!gpsTrackingAllowed) return 'Live sharing disabled';
    if (!gpsTracking.enabled) return 'Your sharing is off';
    if (liveGps.status === 'connected') return gpsTracking.lastFix ? 'Sharing on' : 'Waiting for your location';
    if (liveGps.status === 'connecting') return 'Connecting to live feed...';
    if (liveGps.status === 'error') return 'Live feed unavailable';
    return 'Live sharing idle';
  }, [gpsTrackingAllowed, liveGps.status, gpsTracking.enabled, gpsTracking.lastFix]);

  const showGpsButton = gpsTrackingAllowed;
  const isTrackingActive =
    gpsTracking.trackingState === 'tracking' || gpsTracking.trackingState === 'starting';
  const locationCtaLabel = gpsTracking.permissionState === 'granted' ? 'Start location' : 'Enable location';
  const handleCloseLocationPanel = () => onCloseLocationPanel?.();

  return (
    <div className="relative h-full w-full bg-[#293e27]">
      <MapContainer 
        center={center} 
        zoom={zoom} 
        zoomControl={false}
        attributionControl={false}
        scrollWheelZoom={true} 
        zoomSnap={0.1}
        style={{ height: '100%', width: '100%', backgroundColor: 'inherit' }}
        ref={setMapInstance}
      >
        
        {personalSettings.showZoomControls && <ZoomControl position="topleft" />}
        <MapCredits />
        {user&&activeFarm&&privileges.map?.view&&<MapEnvironment key={JSON.stringify([user.email,activeFarm.farm_id,activeFarm.boundary_geojson,privileges.map.view])} account={user.email} farmId={activeFarm.farm_id} boundary={activeFarm.boundary_geojson} layerControl={layerControl} prepared={preparedTerrain} isHunter={(user.roles??[user.role]).includes('Hunter')}/>}
        {user&&activeFarm&&privileges.map?.view&&<NeighborParcelsLayer key={'neighbors:'+JSON.stringify([user.email,activeFarm.farm_id,activeFarm.boundary_geojson,privileges.map.view,privileges.neighborContacts])} account={user.email} farmId={activeFarm.farm_id} boundary={activeFarm.boundary_geojson} layerControl={layerControl} interactionDisabled={!!onMapClick||!!draftPoint}/>}
        {selectedFeatureId&&onSelectionClose&&<MapSelectionDismissal onClose={onSelectionClose}/>}
        <LayerGroup>
          <OfflineImageryLayer/>
          <TileLayer url={MAP_TILE_URLS.arcgisLabels} errorTileUrl="data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%221%22%20height%3D%221%22%3E%3C%2Fsvg%3E" />
        </LayerGroup>
        <MapLayerMenu onControl={setLayerControl} key={(routeState?.recording?routeState.active?.id:routeState?.selectedRouteId||'base')+':'+(routeState?.recording?routeState.active?.name:routeState?.routes.find(r=>r.id===routeState.selectedRouteId)?.name||'')}>
          <RecordedRouteLayer followingLocation={gpsTracking.followMe&&gpsTracking.nearProperty}/>
          {gpsTrackingAllowed&&<LayersControl.Overlay checked={personalSettings.layers['Live users']!==false} name="Live users"><LayerGroup>{liveGps.peerLocations.map(loc=><PeerLocationMarker key={loc.userSub} location={loc}/>)}</LayerGroup></LayersControl.Overlay>}
          {/* Overlays */}
          
          {farmFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Farm Boundary']!==false} name="Farm Boundary">
                <LayerGroup>
                    {farmFeatures.map(f => (
                        <FeatureRenderer
                          key={f.map_feature_id}
                          feature={f}
                          onClick={onFeatureClick}
                          isSelected={f.map_feature_id === selectedFeatureId}
                          isInteractive={false}
                        />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {tractFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Tracts']!==false} name="Tracts">
                <LayerGroup>
                    {tractFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {fieldFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Fields']!==false} name="Fields">
                <LayerGroup>
                    {fieldFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {structureFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Structures']!==false} name="Structures">
                <LayerGroup>
                    {structureFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {blindFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Blinds']!==false} name="Blinds">
                <LayerGroup>
                    {blindFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {treeStandFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Tree Stands']!==false} name="Tree Stands">
                <LayerGroup>
                    {treeStandFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {poiFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Other Places']!==false} name="Other Places">
                <LayerGroup>
                    {poiFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {trailFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Trails & Roads']!==false} name="Trails & Roads">
                <LayerGroup>
                    {trailFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {otherFeatures.length > 0 && (
             <LayersControl.Overlay checked={personalSettings.layers['Other Features']!==false} name="Other Features">
                <LayerGroup>
                    {otherFeatures.map(f => (
                        <FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id === selectedFeatureId} />
                    ))}
                </LayerGroup>
             </LayersControl.Overlay>
          )}

          {<LayersControl.Overlay checked={personalSettings.layers['Inactive POIs']===true} name="Inactive POIs"><LayerGroup>{inactiveFeatures.map(f=><FeatureRenderer key={f.map_feature_id} feature={f} onClick={onFeatureClick} isSelected={f.map_feature_id===selectedFeatureId}/>)}</LayerGroup></LayersControl.Overlay>}
      </MapLayerMenu>

        {!restoreView&&(!viewResetToken||!geofenceGeoJson)&&!routeState?.selectedRouteId&&!routeState?.recording && <MapBounds key={viewResetToken} features={features} />}
        <MapViewContext restoreView={restoreView} onViewChange={onViewChange}/>
        <PlaceMapClick onClick={onMapClick} />
        {draftPoint && <CircleMarker center={draftPoint} radius={12} pathOptions={{color:"#fff",fillColor:"#d97706",fillOpacity:1,weight:3}}><Tooltip permanent>New place</Tooltip></CircleMarker>}
        <MapResizeHandler onSidebarResize={()=>gpsTracking.recenter(false)} />
        {gpsTrackingAllowed && gpsTracking.lastFix && (
          <CurrentLocationMarker position={gpsTracking.lastFix} headingDeg={compass.heading?.degrees} headingSource={compass.heading?.source} lastKnown={isFixStale} />
        )}

      </MapContainer>

      <button aria-label={gpsTracking.recenterLabel} disabled={!gpsTracking.canRecenter} onClick={()=>gpsTracking.recenterNext()} className="absolute bottom-8 left-4 z-[400] p-3 bg-white rounded-full shadow-lg text-usda-700 hover:bg-usda-50 disabled:opacity-50 disabled:cursor-not-allowed" title={gpsTracking.recenterLabel}><LocateFixed size={20}/></button>
      {showGpsButton && isLocationPanelOpen && (
        <div className="fixed inset-0 z-[1300] bg-black/40 flex items-end sm:items-center sm:justify-center">
          <div className="w-full sm:max-w-lg max-h-[80vh] bg-white rounded-t-2xl sm:rounded-2xl overflow-y-auto shadow-2xl transform transition-all duration-200 translate-y-0 opacity-100 p-4">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 -mx-4 px-4 pb-3 mb-3 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Map / Location</h2>
                <button
                  onClick={handleCloseLocationPanel}
                  className="text-gray-500 hover:text-gray-800"
                  title="Close location panel"
                  aria-label="Close location panel"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 uppercase">Live GPS</div>
                  <div className="text-sm font-bold text-gray-900">{statusText}</div>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                    <div>
                      {gpsTracking.inGeofence === null ? 'Your location is not available yet' : gpsTracking.inGeofence ? 'On property' : 'Off property'}
                    </div>
                    <div>Accuracy: {accuracyText}</div>
                    <div>{compass.heading?.source==='compass'?'Arrow: phone direction':compass.heading?.source==='course'?'Arrow: travel direction':'Direction unavailable'}</div>
                    {gpsTracking.lastFix && <div className={clsx(isFixStale ? 'text-amber-700 font-medium' : '')}>{isFixStale ? 'Last known location' : 'Location updated'}: {stalenessText}</div>}
                    {permissionMessage && <div>{permissionMessage}</div>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={handleCloseLocationPanel}
                    className="self-end text-gray-400 hover:text-gray-700"
                    title="Close location panel"
                    aria-label="Close location panel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <button
                    onClick={gpsTracking.toggleFollow}
                    className={clsx(
                      'text-xs px-2 py-1 rounded border',
                      gpsTracking.followMe ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-700'
                    )}
                    disabled={!gpsTracking.lastFix}
                    title="Toggle follow me"
                  >
                    {gpsTracking.followMe ? 'Following' : 'Follow'}
                  </button>
                  <button
                    onClick={()=>gpsTracking.recenterNext()}
                    className="text-xs px-2 py-1 rounded border bg-white border-gray-200 text-gray-700 disabled:opacity-50"
                    disabled={!gpsTracking.canRecenter}
                    title={gpsTracking.recenterLabel}
                    aria-label={gpsTracking.recenterLabel}
                  >
                    Recenter
                  </button>
                </div>
              </div>
              <div className="mt-3 border-t border-gray-100 pt-2">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase">Other users</div>
                  <div className="text-[11px] text-gray-600">{liveStatusText}</div>
                </div>
                {liveGps.peerLocations.length === 0 ? (
                  <div className="text-xs text-gray-500 mt-1">No other recent locations to show.</div>
                ) : (
                  <div className="mt-1 space-y-1 max-h-28 overflow-y-auto pr-1">
                    {liveGps.peerLocations.map((peer) => (
                      <div key={`${peer.userSub}-${peer.sessionId}`} className="flex items-center gap-2 rounded bg-gray-50 px-2 py-1">
                        <div
                          className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white"
                          style={{ backgroundColor: peer.isLive ? colorFromString(peer.userEmail || peer.userSub) : '#64748b', border: peer.isLive ? 'none' : '2px dashed white' }}
                        >
                          {getInitials(peer.userName, peer.userEmail)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-800 truncate">{peer.userName || peer.userEmail}</div>
                          <div className="text-[11px] text-gray-600">{peer.isLive ? 'Live' : '◷ Last known'} · {formatRelativeTime(peer.coords.ts)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {liveGps.error && (
                  <div className="mt-1 text-[11px] text-red-600">{liveGps.error}</div>
                )}
              </div>
              {gpsTracking.errors.length > 0 && (
                <div className="mt-2 text-[11px] text-red-600 space-y-0.5">
                  {gpsTracking.errors.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </div>
              )}
              <div className="mt-2 text-[11px] text-gray-500">Location and sharing start automatically. Gray markers with a clock show last-seen locations, kept for {Math.round(liveGps.lastKnownTtlMs / 60000)} minutes.</div>
              {gpsTracking.enabled && <button onClick={gpsTracking.stop} className="mt-3 w-full text-sm px-3 py-2 rounded border border-gray-300">Turn off location & sharing</button>}
              {(!gpsTracking.enabled || !isTrackingActive) && (
                <div className="mt-3">
                  <button
                    onClick={() => void gpsTracking.start()}
                    className="w-full text-sm font-semibold px-3 py-2 rounded border bg-blue-600 border-blue-700 text-white hover:bg-blue-700"
                  >
                    {locationCtaLabel}
                  </button>
                </div>
              )}
              {gpsTracking.permissionState === 'denied' && (
                <div className="mt-2 text-[11px] text-gray-600">
                  Location access denied. Please enable browser location for this site.
                </div>
              )}
              {gpsTracking.trackingState === 'starting' && (
                <div className="mt-2 text-[11px] text-gray-600">Acquiring location...</div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
